import React, { useState, useEffect, useCallback } from 'react';

import { HistoryOutlined, ShopOutlined, SoundOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Dropdown, Input, Select, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import operatorApiFetch from '@services/operator/apiService';

const { TextArea } = Input;

/**
 * 頂部導航列的廣播快捷按鈕
 * 點擊後展開發送廣播面板（Dropdown）
 */
export default function BroadcastButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [broadcastType, setBroadcastType] = useState('announcement');
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);
  const [merchantList, setMerchantList] = useState([]);
  const [loading, setLoading] = useState(false);

  // 載入可通知貨主
  const loadMerchants = useCallback(async () => {
    try {
      const response = await operatorApiFetch('warehouse_broadcasts/assignees.json');
      const data = await response.json();
      if (data.success) {
        setMerchantList(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load merchants:', error);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadMerchants();
    }
  }, [open, loadMerchants]);

  // 發送廣播
  const handleSend = async () => {
    if (!content.trim()) {
      message.warning(t('operator.pages.warehouse_broadcasts.content_required'));
      return;
    }

    setLoading(true);
    try {
      const body = {
        content,
        broadcast_type: broadcastType,
      };

      if (broadcastType === 'task' && selectedMerchantIds.length > 0) {
        body.merchant_ids = selectedMerchantIds;
      }

      const response = await operatorApiFetch('warehouse_broadcasts.json', {
        method: 'POST',
        body,
      });
      const data = await response.json();

      if (data.success) {
        message.success(t('operator.pages.warehouse_broadcasts.send_success'));
        setContent('');
        setSelectedMerchantIds([]);
        setBroadcastType('announcement');
        setOpen(false);
      } else {
        message.error(data.message || t('common.error.unknown'));
      }
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      message.error(t('common.error.unknown'));
    } finally {
      setLoading(false);
    }
  };

  const merchantOptions = merchantList.map(m => ({
    label: m.name,
    value: m.id,
  }));

  const broadcastTypeOptions = [
    { label: t('operator.pages.warehouse_broadcasts.type_announcement'), value: 'announcement' },
    { label: t('operator.pages.warehouse_broadcasts.type_task'), value: 'task' },
    { label: t('operator.pages.warehouse_broadcasts.type_urgent'), value: 'urgent' },
  ];

  const panel = (
    <div style={{
      width: 460,
      backgroundColor: 'white',
      boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
      borderRadius: '8px',
      padding: '20px',
    }}>
      {/* 標題 */}
      <div style={{
        fontWeight: 600,
        fontSize: '16px',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SoundOutlined />
          {t('operator.pages.warehouse_broadcasts.send_title')}
        </div>
        <Button
          type="link"
          size="small"
          icon={<HistoryOutlined />}
          onClick={() => {
            setOpen(false);
            navigate('/warehouse_broadcasts');
          }}
        >
          {t('operator.pages.warehouse_broadcasts.history_title')}
        </Button>
      </div>

      {/* 類型選擇 */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}>
          {t('operator.pages.warehouse_broadcasts.type_label')}
        </div>
        <Select
          value={broadcastType}
          onChange={(val) => {
            setBroadcastType(val);
            if (val !== 'task') setSelectedMerchantIds([]);
          }}
          options={broadcastTypeOptions}
          style={{ width: '100%' }}
        />
      </div>

      {/* 任務類型時顯示通知貨主 */}
      {broadcastType === 'task' && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}>
            <ShopOutlined style={{ marginRight: 4 }} />
            {t('operator.pages.warehouse_broadcasts.assignees')}
          </div>
          <Select
            mode="multiple"
            value={selectedMerchantIds}
            onChange={setSelectedMerchantIds}
            options={merchantOptions}
            placeholder={t('operator.pages.warehouse_broadcasts.select_assignees')}
            style={{ width: '100%' }}
            allowClear
            optionFilterProp="label"
          />
        </div>
      )}

      {/* 訊息內容 */}
      <TextArea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t('operator.pages.warehouse_broadcasts.content_placeholder')}
        rows={4}
        maxLength={500}
      />

      {/* 字數統計 + 發送按鈕 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 8,
      }}>
        <span style={{ fontSize: 12, color: '#999' }}>
          {content.length} / 500
        </span>
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={loading}
          onClick={handleSend}
          disabled={!content.trim()}
        >
          {t('operator.pages.warehouse_broadcasts.send_button')}
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => panel}
      trigger={['click']}
      placement="bottomRight"
    >
      <Tooltip title={!open ? t('common.tooltip.broadcast') : undefined}>
        <Button
          type="text"
          icon={<SoundOutlined />}
          className="operator-header-icon"
        />
      </Tooltip>
    </Dropdown>
  );
}
